import api from "@/shared/api";

const unwrapResponse = (response) =>
  response?.data?.data ?? response?.data ?? response ?? [];

export const normalizePriceCardElement = (element = {}, manifestCard = {}) => {
  const priceCard = element.priceCard || element.card || manifestCard || {};

  return {
    ...element,
    id: element.id || element._id || element.elementId,
    name:
      element.name ||
      element.label ||
      element.description ||
      element.code ||
      "",
    description: element.description || element.name || element.label || "",
    unit: element.unit || element.uom || element.unitOfMeasure || "",
    productId: element.productId || element.source?.productId || null,
    unitPrice: Number(
      element.unitPrice ??
        element.pricing?.amount ??
        element.price ??
        element.amount ??
        element.rate ??
        0
    ),
    priceCardId:
      element.priceCardId ||
      element.cardId ||
      priceCard.id ||
      priceCard._id ||
      null,
    priceCardVersion:
      element.priceCardVersion ??
      element.cardVersion ??
      element.version ??
      priceCard.version ??
      null,
  };
};

const normalizeCollection = (payload) => {
  const values = Array.isArray(payload)
    ? payload
    : payload?.elements || payload?.items || payload?.results || [];
  const card = payload?.card || {};
  return values
    .map((element) => normalizePriceCardElement(element, card))
    .filter((element) => element.id);
};

/**
 * Searches elements on the live authenticated price card.
 */
export async function searchLivePriceCardElements(search, options = {}) {
  const response = await api.get("/pricing/elements", {
    params: {
      search: String(search || "").trim(),
      ...options,
    },
  });

  const query = String(search || "").trim().toLowerCase();
  const elements = normalizeCollection(unwrapResponse(response));
  if (!query) return elements;

  return elements.filter((element) =>
    [element.name, element.description, element.type, element.group]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(query))
  );
}

/**
 * Resolves one live element through the collection endpoint. The backend may
 * optimize elementId, while the client also safely filters a collection.
 */
export async function getLivePriceCardElement(elementId) {
  if (!elementId) return null;

  const elements = await searchLivePriceCardElements("", { elementId });
  return (
    elements.find((element) => String(element.id) === String(elementId)) || null
  );
}

export async function getManagedPriceCardElements() {
  const response = await api.get("/pricing/elements/manage");
  return normalizeCollection(unwrapResponse(response));
}

export async function createLivePriceCardElement(element) {
  const response = await api.post("/pricing/elements", element);
  const payload = response?.data?.data ?? response?.data ?? response;
  if (!payload?.element) {
    throw new Error(response?.error || "Price card element was not created");
  }
  return normalizePriceCardElement(payload.element, payload.card);
}

export async function updateLivePriceCardElement(elementId, updates) {
  const response = await api.put(
    `/pricing/elements/${encodeURIComponent(elementId)}`,
    updates,
  );
  const payload = response?.data?.data ?? response?.data ?? response;
  if (!payload?.element) {
    throw new Error(response?.error || "Price card element was not updated");
  }
  return normalizePriceCardElement(payload.element, payload.card);
}

export async function getPriceCardAudit(limit = 50) {
  const response = await api.get("/pricing/audit", { params: { limit } });
  const payload = response?.data?.data ?? response?.data ?? response;
  return Array.isArray(payload) ? payload : [];
}

